import { useMemo, useState } from "react";
import { animeList, genreOptions } from "@/mocks/anime";

export default function Catalog() {
  const [genre, setGenre] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [onlyOngoing, setOnlyOngoing] = useState(false);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return animeList.filter((item) => {
      const matchGenre = genre === "全部" || item.genres.includes(genre);
      const matchOngoing = !onlyOngoing || item.status === "连载中";
      const matchKeyword =
        kw.length === 0 ||
        item.title.toLowerCase().includes(kw) ||
        item.jpTitle.toLowerCase().includes(kw) ||
        item.genres.some((g) => g.includes(kw)) ||
        item.summary.toLowerCase().includes(kw);
      return matchGenre && matchOngoing && matchKeyword;
    });
  }, [genre, keyword, onlyOngoing]);

  const reset = () => {
    setGenre("全部");
    setKeyword("");
    setOnlyOngoing(false);
  };

  return (
    <section id="catalog" className="bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="max-w-2xl">
          <span className="text-sm font-medium text-primary-600">分类检索</span>
          <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">按题材与关键词筛选新番条目</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
            用题材标签快速缩小范围，或直接输入作品名、日文名与关键词进行本地检索。筛选只作用于本页已收录的条目，结果会即时更新。
          </p>
        </div>

        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品名 / 题材 / 关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-700">
                <input
                  type="checkbox"
                  checked={onlyOngoing}
                  onChange={(event) => setOnlyOngoing(event.target.checked)}
                  className="h-4 w-4 accent-primary-500"
                />
                只看连载中
              </label>
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {genreOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setGenre(option)}
                className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                  genre === option
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400 hover:text-secondary-900"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-5 text-xs text-foreground-600">
          共找到 <span className="font-medium text-foreground-900">{list.length}</span> 部符合条件的作品
          {genre !== "全部" && <span> · 题材：{genre}</span>}
          {keyword.trim() && <span> · 关键词：{keyword.trim()}</span>}
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((item) => (
            <article
              key={item.id}
              className="flex gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300"
            >
              <div className="h-24 w-[68px] shrink-0 overflow-hidden rounded-md">
                <img src={item.cover} alt={`${item.title} 条目封面`} className="h-full w-full object-cover object-top" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <h3 className="truncate text-sm font-medium text-foreground-950">{item.title}</h3>
                <p className="mt-1 text-xs text-foreground-600">{item.genres.join(" · ")}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-700">{item.summary}</p>
                <div className="mt-auto flex items-center justify-between pt-2 text-xs">
                  <span className={item.status === "连载中" ? "text-secondary-700" : "text-foreground-600"}>
                    {item.status}
                  </span>
                  <span className="text-accent-700">{item.score} 分</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-500"></i>
            <p className="mt-3 text-sm text-foreground-700">没有匹配的作品，换个题材或关键词再试试。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部作品
            </button>
          </div>
        )}
      </div>
    </section>
  );
}